import React from 'react'
import { Button } from 'antd'
import { checkEmail, isApiError, tokenStore } from '../api'
import { useToast } from './Toast'

const COOLDOWN_SECONDS = 30
const OTP_SENT_MESSAGE = "We've sent a 6-digit code to your email."

/**
 * Re-sends the email OTP by calling `POST /auth/check-email` again for the
 * pending address. Reflects progress in the button label and falls back to the
 * stored pending email when none is passed in. Resending is rate-limited to
 * once every {@link COOLDOWN_SECONDS}s — the cooldown starts on mount (a code
 * was just sent when the user reaches this step) and restarts after each send.
 */
export function ResendCodeButton({ email }: { email?: string }) {
  const { showToast } = useToast()

  const [status, setStatus] = React.useState<'idle' | 'sending' | 'sent' | 'error'>('idle')
  const [cooldown, setCooldown] = React.useState(COOLDOWN_SECONDS)

  // Tick the cooldown down to zero; recreate the interval only when the
  // cooling/idle boundary flips, not on every second.
  const cooling = cooldown > 0
  React.useEffect(() => {
    if (!cooling) return
    const id = setInterval(() => {
      setCooldown((s) => Math.max(0, s - 1))
    }, 1000)
    return () => clearInterval(id)
  }, [cooling])

  const handleResend = async () => {
    if (status === 'sending' || cooldown > 0) return
    const target = email ?? tokenStore.getPendingEmail()
    if (!target) {
      setStatus('error')
      return
    }
    setStatus('sending')
    try {
      await checkEmail(target)
      setStatus('sent')
      setCooldown(COOLDOWN_SECONDS)
      showToast(OTP_SENT_MESSAGE, 'success')
    } catch (err) {
      setStatus('error')
      if (isApiError(err)) {
        showToast(err.message, 'error')
      }
    }
  }

  const label =
    status === 'sending'
      ? 'Sending…'
      : cooldown > 0
        ? `Resend code in ${cooldown}s`
        : status === 'error'
          ? "Couldn't send - please retry."
          : 'Resend code'

  return (
    <Button
      type="link"
      id="btn-otp-email-resend"
      className="!px-0"
      onClick={handleResend}
      disabled={status === 'sending' || cooldown > 0}
    >
      {label}
    </Button>
  )
}
